import _inheritsLoose from "@babel/runtime/helpers/inheritsLoose";
import { Quill } from 'react-quill';
var BaseVideoFormat = Quill.import('formats/video');
var VideoFormatAttributesList = ['src', 'title', 'width', 'height'];

/**
 * A custom block-level Blot for inserting videos supported by the RichTextEditor.
 * Designed to extend and support Quill's built-in Video blot.
 *
 * To add to the editor, register this Blot with Quill using the register API.
 *
 * @extends BaseVideoFormat
 */
var CustomVideo = /*#__PURE__*/function (_BaseVideoFormat) {
  function CustomVideo() {
    return _BaseVideoFormat.apply(this, arguments) || this;
  }
  _inheritsLoose(CustomVideo, _BaseVideoFormat);
  /**
   * Creates a iframe DOM node with src and title attributes
   * Uses Quill's Video blot's create function to sanitize the video source url
   *
   * @param {string/object} value - an string or object containing the url and/or the alt text of the video
   * @returns node - the corresponding DOM for the video
   */
  CustomVideo.create = function create(value) {
    // extract the embed url for Youtube and Vimeo videos
    var extractedUrl = this.extractVideoUrl(typeof value === 'string' ? value : value.url);

    /**
     * super.create(url) assigns the following attributes to the iframe element:
     *  frameborder: 0
     *  allowfullscreen: true
     *  src: this.sanitize(value)
    */
    var node = _BaseVideoFormat.create.call(this, extractedUrl);

    // these values are taken from the smallest media player size recommended by Youtube's Embedded Video Player API
    // source: https://developers.google.com/youtube/youtube_player_demo
    node.setAttribute('width', 480);
    node.setAttribute('height', 270);
    if (extractedUrl === '') {
      node.setAttribute('src', 'about:blank');
    }
    if (typeof value === 'object') {
      node.setAttribute('title', value.title);
    }
    return node;
  }

  /**
   * Returns the format values represented by domNode if it is this Blot's type
   *
   * @param {Node} domNode - The domNode representing the blot
   * @returns {Object} the attribute values represented by the domNode
   */;
  CustomVideo.formats = function formats(domNode) {
    var videoFormats = VideoFormatAttributesList.reduce(function (formats, attribute) {
      if (domNode.hasAttribute(attribute)) {
        formats[attribute] = domNode.getAttribute(attribute);
      }
      return formats;
    }, {});

    // assign the src attribute to videoSrc to differentiate between images and videos
    videoFormats.videoSrc = videoFormats == null ? void 0 : videoFormats.src;
    return videoFormats;
  }

  /**
   * Apply format to blot. Should not pass onto child or another blot
   * If value is null, the name attribute is removed from the blot
   *
   * @param {String} name - Name of the format
   * @param {Any} value - Value of the format
   */;
  var _proto = CustomVideo.prototype;
  _proto.format = function format(name, value) {
    if (VideoFormatAttributesList.indexOf(name) > -1) {
      if (value) {
        this.domNode.setAttribute(name, value);
      } else {
        this.domNode.removeAttribute(name);
      }
    } else {
      _BaseVideoFormat.prototype.format.call(this, name, value);
    }
  }

  /**
   * Extracts the embed url from the video urls for Youtube and Vimeo sites
   * Code sourced from Quill's base theme:
   * https://github.com/quilljs/quill/blob/6fb1532fbdcfb2d5df4830a81e707160a72da47b/themes/base.ts#L300
   *
   * @param {String} url - video url
   * @returns {String} extracted embed url for Youtube and Vimeo videos or url param if string does not match Youtube or Vimeo site urls
   */;
  CustomVideo.extractVideoUrl = function extractVideoUrl(url) {
    // url matchers for youtube video urls
    var match = url.match(/^(?:(https?):\/\/)?(?:(?:www|m)\.)?youtube\.com\/watch.*v=([a-zA-Z0-9_-]+)/) || url.match(/^(?:(https?):\/\/)?(?:(?:www|m)\.)?youtu\.be\/([a-zA-Z0-9_-]+)/);
    if (match) {
      // youtube embed url format
      return (match[1] || 'https') + "://www.youtube.com/embed/" + match[2];
    }

    // url matchers for vimeo video urls
    match = url.match(/^(?:(https?):\/\/)?(?:www\.)?vimeo\.com\/(\d+)/);
    if (match) {
      // vimeo embed url format
      return (match[1] || 'https') + "://player.vimeo.com/video/" + match[2] + "/";
    }
    return url;
  };
  return CustomVideo;
}(BaseVideoFormat);
CustomVideo.blotName = 'video';
CustomVideo.classname = 'ql-video';
CustomVideo.tagName = 'iframe';
export default CustomVideo;